import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Send, AlertCircle, CheckCircle, Paperclip, Trash2 } from 'lucide-react';
import { ticketService } from '../../services/ticketService';
import { attachmentService } from '../../services/attachmentService';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '../../components/ui/combobox';
import { Checkbox } from '../../components/ui/checkbox';
import { Field, FieldContent, FieldDescription, FieldGroup, FieldLabel } from '../../components/ui/field';

interface FormData {
  titulo: string;
  descricao: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA';
  emergencial: boolean;
}

interface FormErrors {
  titulo?: string;
  descricao?: string;
  prioridade?: string;
}

const prioridades = [
  { value: 'BAIXA', label: 'Baixa', description: 'Questões que não afetam o funcionamento' },
  { value: 'MEDIA', label: 'Média', description: 'Problemas que impactam parcialmente o trabalho' },
  { value: 'ALTA', label: 'Alta', description: 'Problemas críticos que impedem o trabalho' }
] as const;

const prioridadeItems = prioridades.map((p) => p.value);
const prioridadeLabel = (value: string) => prioridades.find((p) => p.value === value)?.label ?? value;
const prioridadeDescription = (value: string) => prioridades.find((p) => p.value === value)?.description ?? '';

export function PortalNovoTicket() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    titulo: '',
    descricao: '',
    prioridade: 'MEDIA',
    emergencial: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Estados para upload de anexos
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.titulo.trim()) {
      newErrors.titulo = 'Título é obrigatório';
    } else if (formData.titulo.trim().length < 5) {
      newErrors.titulo = 'Título deve ter no mínimo 5 caracteres';
    }

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    } else if (formData.descricao.trim().length < 10) {
      newErrors.descricao = 'Descrição deve ter no mínimo 10 caracteres';
    }

    if (!formData.prioridade) {
      newErrors.prioridade = 'Prioridade é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Criar ticket
      const ticket = await ticketService.create({
        titulo: formData.titulo.trim(),
        descricao: formData.descricao.trim(),
        prioridade: formData.prioridade,
        emergencial: formData.emergencial,
        clientId: '' // Será ignorado pelo backend para clientes
      });

      // Upload de anexos se houver
      if (selectedFiles.length > 0) {
        setIsUploading(true);
        for (const file of selectedFiles) {
          await attachmentService.upload(ticket.id, file);
        }
      }

      setSubmitSuccess(true);
      
      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate('/portal/tickets');
      }, 2000);

    } catch (error: any) {
      console.error('Erro ao criar ticket:', error);
      
      if (error.response?.data?.details) {
        // Erros de validação do backend
        const backendErrors: FormErrors = {};
        error.response.data.details.forEach((detail: any) => {
          backendErrors[detail.field as keyof FormErrors] = detail.message;
        });
        setErrors(backendErrors);
      } else {
        setErrors({
          titulo: error.response?.data?.error || 'Erro ao criar ticket. Tente novamente.'
        });
      }
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };

  const handleInputChange = (field: 'titulo' | 'descricao' | 'prioridade', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (submitSuccess) {
    return (
      <div className="space-y-6">
        <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-12 text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Ticket Criado com Sucesso!
          </h1>
          <p className="text-muted-foreground mb-6">
            Seu chamado foi registrado e nossa equipe será notificada. 
            Você receberá atualizações sobre o progresso do atendimento.
          </p>
          <div className="text-sm text-muted-foreground">
            Redirecionando para a lista de tickets...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link 
          to="/portal/tickets"
          className="p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-foreground">Novo Ticket</h1>
          <p className="text-muted-foreground">
            Descreva seu problema ou solicitação para nossa equipe de suporte
          </p>
        </div>
      </div>

      {/* Formulário */}
      <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Título */}
          <div className="space-y-2">
            <label htmlFor="titulo" className="block text-sm font-medium text-foreground">
              Título do Chamado *
            </label>
            <input
              id="titulo"
              type="text"
              value={formData.titulo}
              onChange={(e) => handleInputChange('titulo', e.target.value)}
              placeholder="Ex: Problema no sistema de vendas"
              className={`w-full px-4 py-3 bg-background/50 border rounded-lg text-foreground placeholder:text-muted-foreground transition-colors ${
                errors.titulo 
                  ? 'border-red-500/50 focus:border-red-500' 
                  : 'border-border/50 focus:border-primary'
              }`}
              disabled={isSubmitting}
            />
            {errors.titulo && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                {errors.titulo}
              </div>
            )}
          </div>

          {/* Prioridade */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Prioridade *
            </label>
            <Combobox
              items={prioridadeItems}
              value={formData.prioridade}
              onValueChange={(value) => handleInputChange('prioridade', value)}
              itemToLabel={prioridadeLabel}
            >
              <ComboboxInput
                placeholder="Selecione a prioridade"
                disabled={isSubmitting}
              />
              <ComboboxContent searchPlaceholder="Buscar prioridade...">
                <ComboboxEmpty>Nenhuma prioridade encontrada.</ComboboxEmpty>
                <ComboboxList>
                  {(item) => (
                    <ComboboxItem key={item} value={item}>
                      <span className="flex flex-col">
                        <span className="font-medium">{prioridadeLabel(item)}</span>
                        <span className="text-xs text-muted-foreground">
                          {prioridadeDescription(item)}
                        </span>
                      </span>
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            {errors.prioridade && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                {errors.prioridade}
              </div>
            )}
          </div>

          {/* Situação Emergencial */}
          <FieldGroup>
            <Field orientation="horizontal">
              <Checkbox
                id="emergencial"
                checked={formData.emergencial}
                onCheckedChange={(checked: boolean | 'indeterminate') =>
                  setFormData((prev) => ({ ...prev, emergencial: checked === true }))
                }
                disabled={isSubmitting}
              />
              <FieldContent>
                <FieldLabel htmlFor="emergencial">
                  Situação Emergencial
                </FieldLabel>
                <FieldDescription>
                  Ao marcar, o ticket já é criado como aprovado — atendimento
                  imediato, sem passar pela aprovação da estimativa.
                </FieldDescription>
              </FieldContent>
            </Field>
          </FieldGroup>

          {/* Descrição */}
          <div className="space-y-2">
            <label htmlFor="descricao" className="block text-sm font-medium text-foreground">
              Descrição Detalhada *
            </label>
            <textarea
              id="descricao"
              value={formData.descricao}
              onChange={(e) => handleInputChange('descricao', e.target.value)}
              placeholder="Descreva detalhadamente o problema ou solicitação. Inclua informações como quando o problema começou, quais ações você já tentou, mensagens de erro, etc."
              rows={6}
              className={`w-full px-4 py-3 bg-background/50 border rounded-lg text-foreground placeholder:text-muted-foreground transition-colors resize-none ${
                errors.descricao 
                  ? 'border-red-500/50 focus:border-red-500' 
                  : 'border-border/50 focus:border-primary'
              }`}
              disabled={isSubmitting}
            />
            {errors.descricao && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                {errors.descricao}
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              Mínimo 10 caracteres. Quanto mais detalhes, melhor poderemos ajudar.
            </div>
          </div>

          {/* Upload de Anexos */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              Anexos (opcional)
            </label>
            
            <div className="space-y-3">
              {/* Botão de upload */}
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  id="fileUpload"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.zip"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isSubmitting || isUploading}
                />
                <label
                  htmlFor="fileUpload"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-muted/50 hover:bg-muted border border-border/50 rounded-lg cursor-pointer transition-colors text-sm"
                >
                  <Paperclip className="w-4 h-4" />
                  Anexar Evidências
                </label>
                <span className="text-xs text-muted-foreground">
                  PDF, imagens, TXT, CSV, ZIP (máx. 10MB cada)
                </span>
              </div>

              {/* Lista de arquivos selecionados */}
              {selectedFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Arquivos selecionados:</p>
                  <div className="space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-background/50 border border-border/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span>{attachmentService.getFileIcon(file.type)}</span>
                          <div>
                            <p className="text-sm font-medium text-foreground">{file.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {attachmentService.formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSelectedFile(index)}
                          className="p-1 hover:bg-red-500/10 rounded text-red-500 transition-colors"
                          disabled={isSubmitting || isUploading}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="text-xs text-muted-foreground">
                Anexe capturas de tela, logs de erro, documentos ou qualquer evidência que ajude nossa equipe a entender melhor o problema.
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex items-center gap-4 pt-4">
            <button
              type="submit"
              disabled={isSubmitting || isUploading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting || isUploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  {isUploading ? 'Enviando anexos...' : 'Criando Ticket...'}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Criar Ticket
                </>
              )}
            </button>
            
            <Link
              to="/portal/tickets"
              className="inline-flex items-center gap-2 px-6 py-3 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted transition-colors"
            >
              Cancelar
            </Link>
          </div>
        </form>
      </div>

      {/* Informações Adicionais */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <AlertCircle className="w-4 h-4 text-blue-600" />
          </div>
          <div className="space-y-2">
            <h3 className="font-medium text-foreground">Dicas para um atendimento mais rápido</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Seja específico sobre o problema ou solicitação</li>
              <li>• Inclua capturas de tela se aplicável</li>
              <li>• Mencione quando o problema começou</li>
              <li>• Descreva os passos que você já tentou</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}